import TurnedInNotIcon from '@mui/icons-material/TurnedInNot';
import { Box, Button, Tooltip } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { ThresholdsColumns } from 'features/inventory/warehouses/thresholds';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { IsSuperAdmin, IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTechnicianCoordinator } from 'shared/permissions/useIsTechnicianCoordinator';
import { ContractLine } from 'features/inventory/contracts/types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { labelThresholdService } from 'features/inventory/warehouses/api/label-threshold.service';
import { Option, OptionDescription, StatusOption } from 'shared/types/general';
import toast from 'shared/utils/toast';
import LabelThresholdForm from '../form/LabelThresholdForm';
import ProductWarehouseForm from '../form/ProductWarehouseForm';
import WarehouseContractLineForm from '../form/WarehouseContractLineForm';
import { contractLineService, productService, contractService } from 'shared/services';
import { productWarehouseService } from '../../api/product-warehouse.service';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import { ContractLinesWarehouseColumns } from '../../contract-lines-warehouse';

interface ThresholdTabProps {
  warehouseId: number;
  products: Option[];
  categories: OptionDescription[];
  contracts: any[];
  statusOptions: StatusOption[];
  labelOptions: OptionDescription[];
  translationPrefix: string;
}

export function ThresholdTab({
  warehouseId,
  products,
  categories,
  contracts,
  statusOptions,
  labelOptions,
  translationPrefix
}: ThresholdTabProps) {
  const { t } = useTranslation();
  const thresholdsRefresh = useRef<(() => void) | null>(null);

  const isDisabledEdit = useIsTechnicianCoordinator() || useIsTechnician();
  const isSuperAdmin = IsSuperAdminGuard();

  const [openThresholdDialog, setOpenThresholdDialog] = useState(false);
  const [openLabelDialog, setOpenLabelDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [openContractLineDialog, setOpenContractLineDialog] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [contractLines, setContractLines] = useState<ContractLine[]>([]);

  /* ---------- Handlers soglie ---------- */
  const handleCloseThresholdDialog = () => setOpenThresholdDialog(false);
  const handleCloseLabelDialog = () => setOpenLabelDialog(false);
  const handleCloseImportDialog = () => setOpenImportDialog(false);
  const handleCloseContractDialog = () => setOpenContractLineDialog(false);

  const addThreshold = async (values: Record<string, unknown>) => {
    values['warehouse'] = +warehouseId;

    const label = labelOptions.find((label) => label?.id === values?.label)?.code;
    if (label) values['label'] = label;
    const res = await productWarehouseService.createUnifiedInstance(values);
    if (res) {
      toast.success(t(`${translationPrefix}.threshold-created`));
      thresholdsRefresh.current();
    }
    handleCloseThresholdDialog();
  };

  const createLabelThreshold = async (values) => {
    await labelThresholdService.add(values);
    toast.success(t(`${translationPrefix}.label.created`));
    thresholdsRefresh.current?.();
    handleCloseLabelDialog();
  };

  /* ---------- Handlers contratti / prezzi ---------- */
  const onManagePrices = (product) => {
    contractLineService
      .getAllBaseInformation({
        product: product,
        warehouse_id: warehouseId
      })
      .then((res) => {
        setContractLines(res);
      });
    setSelectedProduct(product);
    setOpenContractLineDialog(true);
  };

  const handleDeleteContractLine = async (id: number) => {
    await contractLineService.delete(id);
    if (selectedProduct) onManagePrices(selectedProduct);
  };

  const createContractLine = async (values) => {
    productService.getAllBaseInformation({ code: selectedProduct }).then((product) => {
      const productId = product[0].id;
      const lineToAdd: ContractLine = {
        id: Math.random() * 1000000,
        product: productId,
        unit_price: values.unit_price.replace(',', '.'),
        initial_available_units: values.initial_available_units,
        available_units: values.initial_available_units,
        used_units: 0,
        initial_discounted_units: values.initial_discounted_units,
        available_discounted_units: values.initial_discounted_units,
        used_discounted_units: 0,
        warehouse: +warehouseId,
        iva: values.iva
      };
      contractService.assignProducts([lineToAdd], values.contract_header).then(() => {
        toast.success(t(`${translationPrefix}.contractline-created`));

        if (selectedProduct) {
          onManagePrices(selectedProduct);
        }
      });
    });
  };

  /* ---------- Colonne ---------- */
  const thresholdColumns: GridColDef[] = ThresholdsColumns(
    true,
    onManagePrices,
    thresholdsRefresh,
    statusOptions,
    categories
  );

  const contractLineColumns: GridColDef[] = ContractLinesWarehouseColumns(handleDeleteContractLine);

  return (
    <>
      <EnhancedCard
        title={t('menu.thresholds')}
        buttons={
          !isDisabledEdit && (
            <>
              <Box className="px-4">
                <IsSuperAdmin>
                  <Button onClick={() => setOpenImportDialog(true)} variant="outlined" size="small">
                    {t(`${translationPrefix}.import-thresholds-costs`)}
                  </Button>
                </IsSuperAdmin>
              </Box>
              <Box>
                <Tooltip title={t(`${translationPrefix}.label.add-label`)}>
                  <Button variant="outlined" size="small" onClick={() => setOpenLabelDialog(true)}>
                    <TurnedInNotIcon />
                  </Button>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenThresholdDialog(true)}>
                  {t(`${translationPrefix}.label.add-threshold`)}
                </Button>
              </Box>
            </>
          )
        }>
        <EnhancedDataGrid
          service={productWarehouseService}
          columns={thresholdColumns}
          refresh={thresholdsRefresh}
          outerParams={{ warehouse_id: `${warehouseId}` }}
        />
      </EnhancedCard>

      {/* Dialog: nuova soglia */}
      <EnhancedDialog
        open={openThresholdDialog}
        onClose={handleCloseThresholdDialog}
        title={t(`${translationPrefix}.add-threshold`)}
        submitLabel="Aggiungi"
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="md"
        submitButtonProps={{ type: 'submit', form: 'addProductThreshold' }}>
        <ProductWarehouseForm
          products={products}
          categories={categories}
          statusOptions={statusOptions}
          contracts={contracts}
          onSubmit={addThreshold}
          labelOptions={labelOptions}
          warehouseId={warehouseId}
        />
      </EnhancedDialog>

      {/* Dialog: nuova label */}
      <EnhancedDialog
        open={openLabelDialog}
        onClose={handleCloseLabelDialog}
        title={t(`${translationPrefix}.label.add-title`)}
        submitLabel="Aggiungi"
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="sm"
        submitButtonProps={{ type: 'submit', form: 'addLabelThreshold' }}>
        <LabelThresholdForm onSubmit={createLabelThreshold} />
      </EnhancedDialog>

      {/* Dialog: import */}
      <ImportFileUploader
        service={productWarehouseService}
        open={openImportDialog}
        onClose={handleCloseImportDialog}
        hideTriggerButton
        onUploadSuccess={handleCloseImportDialog}
        importMethod={(file: File) =>
          productWarehouseService.importCostsFromFile(file, warehouseId)
        }
        templateGetterMethod={productWarehouseService.getCostsImportTemplate}
      />

      {/* Dialog: righe contrattuali (prezzi) */}
      <EnhancedDialog
        open={openContractLineDialog}
        onClose={handleCloseContractDialog}
        title={t(`${translationPrefix}.contractline-list-menu`)}
        submitLabel="Aggiungi"
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="lg"
        submitButtonProps={{ type: 'submit', form: 'addContractLine' }}>
        <Box mb={3}>
          <WarehouseContractLineForm onSubmit={createContractLine} contracts={contracts} />
        </Box>
        <DataGridPremium
          density="compact"
          autoHeight
          rows={contractLines ?? []}
          columns={contractLineColumns}
        />
      </EnhancedDialog>
    </>
  );
}
